import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PLP_PAGE_TYPE } from '../product-list-page-constants';

import {
  useDataLayer,
  useProductEntryDataLayerValues,
  useTreadwellPreferences
} from '../../../shared/hooks/hooks';

import {
  getPdlDataLayerValues,
  getProductCoreStatus,
  getProductImpressions
} from '../../../shared/utils/product/product';
import { sessionStorage } from '../../../shared/utils/storage/storage';
import { isArrayWithLength } from '../../../shared/utils/validators/validators';

import {
  FILTERS_APPLIED,
  FILTERS_APPLIED_COUNT,
  FILTERS_PATH,
  PAGE_CATEGORY,
  PAGE_NAME,
  PAGE_SECTION_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE,
  PRODUCT_LIST,
  PRODUCT_LIST_CORE_STATUS,
  PRODUCT_LIST_PAGINATION_PAGE,
  PRODUCT_LIST_RELATIVE_POSITION,
  PRODUCT_LIST_SORT,
  TOTAL_NUMBER_OF_PAGES,
  TOTAL_NUMBER_OF_RESULTS
} from '../../../shared/constants/data-layer';
import { PRODUCT_TYPES } from '../../../shared/constants/products';
import { PLP_PRODUCT_SEARCH_ID } from '../../../shared/constants/storage-keys';

export default function usePlpAnalytics(
  products,
  productType,
  selectedFilters,
  currentSort,
  isLoading,
  isPrebuilt,
  pageNum,
  numberOfPages,
  category
) {
  const [shouldSendAnalytics, setShouldSendAnalytics] = useState(false);
  const { search: queryParams } = useLocation();
  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { updateDataLayer } = useDataLayer({
    shouldResetPageOnFitmentChange: true
  });

  const prevQueryParamsRef = useRef(queryParams);

  const categoryName = category?.name;
  let pageName = '';

  if (productType === PRODUCT_TYPES.TIRES) {
    pageName = 'tire_list';
  } else if (productType === PRODUCT_TYPES.WHEELS) {
    pageName = 'wheel_list';
  } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
    pageName = 'accessory_list';
  } else if (isPrebuilt) {
    pageName = 'prebuilt_list';
  }

  const getDataLayerValues = useCallback(
    products => {
      const filtersPath =
        queryParams === prevQueryParamsRef.current ? 'Entry' : 'Apply Filter';
      const defaultAppliedFilters = isTreadwellFlow ? ['Treadwell'] : [];
      let appliedFilters = defaultAppliedFilters;
      let appliedFiltersCount = '0';
      let pageSubcategory = 'product search results';

      if (isPrebuilt) {
        pageSubcategory = 'prebuilt product list';
      }

      if (isArrayWithLength(selectedFilters)) {
        appliedFilters = selectedFilters.map(f => `${f.valueName}:`);
        appliedFiltersCount = selectedFilters.length.toString();
      }
      if (categoryName) {
        appliedFilters.unshift(`${categoryName}`);
      }

      const searchIdString = `${filtersPath}_${appliedFilters}_${currentSort}`;
      sessionStorage(PLP_PRODUCT_SEARCH_ID, searchIdString);

      const productEntryDataLayerValues = products?.map(
        getProductEntryDataLayerValues
      );
      const page = pageNum + 1;

      return {
        [FILTERS_APPLIED]: appliedFilters,
        [FILTERS_APPLIED_COUNT]: appliedFiltersCount,
        [FILTERS_PATH]: filtersPath,
        [PAGE_CATEGORY]: 'product search',
        [PAGE_NAME]: pageName,
        [PAGE_SECTION_NAME]: document.title,
        [PAGE_SUBCATEGORY]: pageSubcategory,
        [PAGE_TYPE]: PLP_PAGE_TYPE,
        [PRODUCT_LIST]: searchIdString,
        [PRODUCT_LIST_CORE_STATUS]: products?.map(getProductCoreStatus),
        [PRODUCT_LIST_RELATIVE_POSITION]: products?.map((_, i) =>
          String(i + 1)
        ),
        [PRODUCT_LIST_SORT]: currentSort?.toLowerCase(),
        [TOTAL_NUMBER_OF_PAGES]: numberOfPages,
        // TODO: if we ever default __FF_LOAD_MORE__ to true and eventually remove
        // the FF, we should remove TOTAL_NUMBER_OF_RESULTS altogether.  It's
        // being renamed to PRODUCT_LIST_PAGINATION_PAGE for load more.
        [process.env.__FF_LOAD_MORE__ === 'true'
          ? PRODUCT_LIST_PAGINATION_PAGE
          : TOTAL_NUMBER_OF_RESULTS]: page,
        ...getProductImpressions(productEntryDataLayerValues, searchIdString),
        ...(isTreadwellFlow
          ? getPdlDataLayerValues(products, treadwellPreferences)
          : null)
      };
    },
    [
      isTreadwellFlow,
      isPrebuilt,
      selectedFilters,
      treadwellPreferences,
      pageName,
      pageNum,
      numberOfPages,
      currentSort,
      queryParams,
      categoryName,
      getProductEntryDataLayerValues
    ]
  );

  const updatePlpAnalytics = useCallback(
    (products, shouldCreateNewEntry = true) => {
      updateDataLayer(getDataLayerValues(products), shouldCreateNewEntry);
      prevQueryParamsRef.current = queryParams;
    },
    [getDataLayerValues, queryParams, updateDataLayer]
  );

  useEffect(() => {
    if (isLoading) {
      setShouldSendAnalytics(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isLoading === false && shouldSendAnalytics) {
      setShouldSendAnalytics(false);
      updatePlpAnalytics(
        process.env.__FF_LOAD_MORE__ === 'true'
          ? products
              ?.filter(productObj => productObj.pageNum === pageNum)
              ?.map(productObj => productObj.product)
          : products
      );
    }
  }, [isLoading, shouldSendAnalytics, updatePlpAnalytics, pageNum, products]);

  return { updatePlpAnalytics };
}
